import React from 'react';
import type { SubmitHandler } from 'react-hook-form';

import type { FormFields } from './types';
import type { Transaction } from 'types/api/transaction';
import type { CsvExportParams } from 'types/client/address';

import config from 'configs/app';
import buildUrl from 'lib/api/buildUrl';
import type { ResourceName } from 'lib/api/resources';
import {
  getCsvExportApiTimeZone,
  localizeCsvExportContent,
  serializeCsvExportDateTime,
  shouldLocalizeCsvExportResponse,
} from 'lib/date/csvExport';
import downloadBlob from 'lib/downloadBlob';
import useToast from 'lib/hooks/useToast';
import useReCaptcha from 'ui/shared/reCaptcha/useReCaptcha';

interface Params {
  hash?: string;
  resource: ResourceName;
  filterType?: CsvExportParams['filterType'] | null;
  filterValue?: CsvExportParams['filterValue'] | null;
  typeFilter?: string;
  tokenAddressHashFilter?: string;
  fileNameTemplate: string;
  exportType: CsvExportParams['type'] | undefined;
  onComplete?: () => void;
  items?: Array<Transaction>;
}

const sanitizeTimeForFileName = (value: string) => value.replaceAll(':', '-');
const CSV_EXPORT_ACCEPT_HEADER = 'application/csv, text/csv, application/octet-stream';

const getCsvExportErrorMessage = async(response: Response) => {
  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    const payload = await response.json().catch(() => undefined) as { message?: string } | undefined;

    if (payload?.message) {
      return payload.message;
    }
  }

  const text = await response.text().catch(() => '');
  return text || 'Something went wrong. Try again later.';
};

export default function useCsvExport({
  hash,
  resource,
  filterType,
  filterValue,
  typeFilter,
  tokenAddressHashFilter,
  fileNameTemplate,
  exportType,
  onComplete,
  items,
}: Params) {
  const toast = useToast();
  const recaptcha = useReCaptcha();
  const isCurrentPageExport = items !== undefined;

  const onFormSubmit = React.useCallback<SubmitHandler<FormFields>>(async(data) => {
    try {
      const recaptchaToken = config.features.csvExport.isEnabled ? await recaptcha.executeAsync() : undefined;

      if (config.features.csvExport.isEnabled && !recaptchaToken) {
        throw new Error('ReCaptcha is not solved');
      }

      const isAddressTokenTransfers = resource === 'csv_export_address_token_transfers';
      const apiTimeZone = getCsvExportApiTimeZone(resource);
      const fromPeriod = serializeCsvExportDateTime(data.from, apiTimeZone);
      const toPeriod = serializeCsvExportDateTime(data.to, apiTimeZone);

      let pathParams: { hash: string } | undefined;
      if ((isAddressTokenTransfers || exportType === 'holders') && hash) {
        pathParams = { hash };
      }

      const url = buildUrl(
        resource,
        pathParams as never,
        isAddressTokenTransfers ? {
          from_period: fromPeriod,
          to_period: toPeriod,
          filter: filterValue || undefined,
          type: typeFilter || undefined,
          token: tokenAddressHashFilter || undefined,
          recaptcha_response: recaptchaToken,
        } : {
          address_id: exportType !== 'holders' && resource !== 'csv_export_token_detail_transfers' ? hash : undefined,
          token_contract_address: resource === 'csv_export_token_detail_transfers' ? hash : undefined,
          from_period: exportType !== 'holders' ? fromPeriod : undefined,
          to_period: exportType !== 'holders' ? toPeriod : undefined,
          filter_type: hash ? filterType || undefined : undefined,
          filter_value: hash ? filterValue || undefined : undefined,
          type: typeFilter || undefined,
          token_address_hash: tokenAddressHashFilter || undefined,
          recaptcha_response: recaptchaToken,
        },
      );

      const response = await fetch(url, {
        headers: isAddressTokenTransfers ? {
          Accept: 'application/json',
        } : {
          Accept: CSV_EXPORT_ACCEPT_HEADER,
        },
      });

      if (!response.ok) {
        throw new Error(await getCsvExportErrorMessage(response));
      }

      const responseText = await response.text();
      const shouldLocalize = exportType !== 'holders' &&
        shouldLocalizeCsvExportResponse(response.headers.get('content-type'), responseText);
      const csvText = shouldLocalize ?
        localizeCsvExportContent(responseText, apiTimeZone) :
        responseText;
      const blob = new Blob([ csvText ], {
        type: shouldLocalize ? 'text/csv;charset=utf-8;' : (response.headers.get('content-type') || 'text/csv;charset=utf-8;'),
      });
      const fileName = exportType === 'holders' ?
        `${ fileNameTemplate }_${ hash }.csv` :
        [
          fileNameTemplate,
          hash,
          `${ data.from.date }_${ sanitizeTimeForFileName(data.from.time) }`,
          `${ data.to.date }_${ sanitizeTimeForFileName(data.to.time) }`,
          filterType && filterValue ? `with_filter_type_${ filterType }_value_${ filterValue }` : undefined,
        ].filter(Boolean).join('_') + '.csv';

      downloadBlob(blob, fileName);
      onComplete?.();
    } catch (error) {
      toast({
        position: 'top-right',
        title: 'Error',
        description: (error as Error)?.message || 'Something went wrong. Try again later.',
        status: 'error',
        variant: 'subtle',
        isClosable: true,
      });
    }
  }, [ exportType, fileNameTemplate, filterType, filterValue, hash, onComplete, recaptcha, resource, toast, tokenAddressHashFilter, typeFilter ]);

  return {
    onFormSubmit,
    recaptcha,
    isCurrentPageExport,
  };
}
